/**
 * ParticleSystem — tiny fading circles (absorption puffs, dash exhaust,
 * milestone spark) and the floating "+N" numbers. Also renders the player
 * cell's trailing tail.
 *
 * Listens on 'fx:particles' and 'fx:floatingText'. Pools Graphics objects
 * for particles so we can comfortably run hundreds at 60fps.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { bus } from '@/core/EventBus';
import type { World } from '@/core/ECS';
import { PlayerCell, Position } from '@/components';
import { rand, randRange } from '@/utils/rng';

interface ParticleObj {
  g: Graphics;
  x: number; y: number;
  vx: number; vy: number;
  age: number; lifetime: number;
  size: number;
  color: number;
}

interface FloatingObj {
  t: Text;
  age: number; lifetime: number;
  vy: number;
}

// Hard caps on concurrent FX so big absorption batches (eating a whole
// shape under a high combo while predators swarm) can't overwhelm the
// renderer. Excess emissions are dropped — visually almost identical
// to the player but keeps the frame budget bounded.
const MAX_PARTICLES = 220;
const MAX_FLOATERS = 14;

export class ParticleSystem {
  private particles: ParticleObj[] = [];
  private floats: FloatingObj[] = [];
  private pool: Graphics[] = [];
  private trailG: Graphics;

  private textStyle: TextStyle;

  private removers: Array<() => void> = [];

  constructor(private parent: Container) {
    this.trailG = new Graphics();
    parent.addChild(this.trailG);

    this.textStyle = new TextStyle({
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: 14,
      fontWeight: '600',
      fill: 0xffffff,
      align: 'center',
    });

    this.removers.push(bus.on('fx:particles', ({ x, y, count, color, spread }) => {
      this.emit(x, y, count, color, spread ?? 90);
    }));
    this.removers.push(bus.on('fx:floatingText', ({ x, y, text, color }) => {
      this.emitText(x, y, text, color);
    }));
  }

  /**
   * Clear all live particles and floating texts without tearing down bus
   * subscriptions. Safe to call on stage restart; the trail graphic is
   * re-attached to the parent in case the caller just did parent.removeChildren().
   */
  reset(): void {
    for (const p of this.particles) {
      p.g.clear();
      p.g.parent?.removeChild(p.g);
      this.pool.push(p.g);
    }
    this.particles.length = 0;
    for (const f of this.floats) {
      f.t.parent?.removeChild(f.t);
      f.t.destroy();
    }
    this.floats.length = 0;
    this.trailG.clear();
    if (this.trailG.parent !== this.parent) this.parent.addChild(this.trailG);
  }

  /** Unsubscribe listeners. Call if the system is truly being disposed. */
  destroy(): void {
    for (const r of this.removers) r();
    this.removers.length = 0;
    this.reset();
  }

  update(dt: number, world: World): void {
    // particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.age += dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= 0.92;
      p.vy *= 0.92;
      const t = p.age / p.lifetime;
      if (t >= 1) {
        p.g.clear();
        p.g.parent?.removeChild(p.g);
        this.pool.push(p.g);
        this.particles.splice(i, 1);
        continue;
      }
      const alpha = 1 - t;
      const s = p.size * (1 - t * 0.4);
      p.g.clear();
      p.g.circle(0, 0, s * 2).fill({ color: p.color, alpha: alpha * 0.18 });
      p.g.circle(0, 0, s).fill({ color: p.color, alpha: alpha * 0.9 });
      p.g.position.set(p.x, p.y);
    }

    // floaters
    for (let i = this.floats.length - 1; i >= 0; i--) {
      const f = this.floats[i];
      f.age += dt;
      const t = f.age / f.lifetime;
      if (t >= 1) {
        f.t.parent?.removeChild(f.t);
        // Pixi Text holds a backing texture; destroy() releases it.
        // Without this, repeated absorptions slowly leak GPU memory.
        f.t.destroy();
        this.floats.splice(i, 1);
        continue;
      }
      f.t.y += f.vy * dt;
      f.vy *= 0.96;
      f.t.alpha = 1 - t * t;
      f.t.scale.set(1 + (1 - (1 - t) * (1 - t)) * 0.15);
    }

    // player trail
    this.trailG.clear();
    for (const e of world.query([PlayerCell, Position])) {
      const cell = world.get(e, PlayerCell)!;
      const pos = world.get(e, Position)!;
      let i = 0;
      for (const sample of cell.trail) {
        const t = sample.age / 0.5;
        if (t >= 1) continue;
        const alpha = (1 - t) * 0.22;
        const r = cell.radius * (1 - t * 0.5);
        this.trailG.circle(sample.x, sample.y, r).fill({ color: 0x4FD1C5, alpha });
        i++;
      }
      void i; void pos;
    }
  }

  private obtain(): Graphics {
    const g = this.pool.pop() ?? new Graphics();
    this.parent.addChild(g);
    return g;
  }

  private emit(x: number, y: number, count: number, color: number, spread: number): void {
    const room = MAX_PARTICLES - this.particles.length;
    if (room <= 0) return;
    const n = Math.min(count, room);
    for (let i = 0; i < n; i++) {
      const a = rand() * Math.PI * 2;
      const s = randRange(spread * 0.4, spread);
      const g = this.obtain();
      this.particles.push({
        g,
        x, y,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s,
        age: 0,
        lifetime: 0.5 + rand() * 0.4,
        size: 1.5 + rand() * 2.2,
        color,
      });
    }
  }

  private emitText(x: number, y: number, text: string, color: number): void {
    if (this.floats.length >= MAX_FLOATERS) return;
    const t = new Text({ text, style: { ...this.textStyle, fill: color } as any });
    t.anchor.set(0.5);
    t.position.set(x, y - 18);
    this.parent.addChild(t);
    this.floats.push({
      t, age: 0, lifetime: 0.9, vy: -60,
    });
  }
}
